import { APP_BOTTOM_BAR_HEIGHT } from "@/constants/layout";
import { isVereinAreaPath, routes } from "@/constants/routes";
import { useTheme } from "@/theme/ThemeProvider";
import { useOptionalFinderMode } from "@/ui/finder/FinderModeContext";
import { finderModeAllowsAppNavigation } from "@/ui/finder/finderMode";
import { OpenDrawerContext } from "@/ui/layout/OpenDrawerContext";
import { usePathname, useRouter } from "expo-router";
import {
  CalendarBlankIcon,
  HouseIcon,
  ListIcon,
  PawPrintIcon,
  UsersThreeIcon,
} from "phosphor-react-native";
import React, { useEffect, useState } from "react";
import { Platform, Pressable, StyleSheet, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { bottomBarHeight } from "@/ui/layout/bottomBarInset";

function useEnvSafeAreaBottom(): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    const probe = document.createElement("div");
    probe.setAttribute("data-safe-area-probe", "bottom");
    probe.style.position = "fixed";
    probe.style.left = "0";
    probe.style.bottom = "0";
    probe.style.visibility = "hidden";
    probe.style.pointerEvents = "none";
    probe.style.paddingBottom = "env(safe-area-inset-bottom)";
    document.body.appendChild(probe);
    const read = () => {
      const parsed = Number.parseFloat(getComputedStyle(probe).paddingBottom);
      setValue(Number.isFinite(parsed) ? parsed : 0);
    };
    read();
    window.addEventListener("resize", read);
    return () => {
      window.removeEventListener("resize", read);
      probe.remove();
    };
  }, []);
  return value;
}

const SIDE_ICON_SIZE = 22;

function pageCurrent(active: boolean): {
  accessibilityCurrent?: "page";
  "aria-current"?: "page";
} {
  if (!active) return {};
  return { accessibilityCurrent: "page", "aria-current": "page" };
}

/** Narrow: Board, Tiere, Verein, Kalender, Menü. */
export function AppBottomBar() {
  const { theme } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const { openDrawer } = React.useContext(OpenDrawerContext);
  const insets = useSafeAreaInsets();
  const envInset = useEnvSafeAreaBottom();
  const finderMode = useOptionalFinderMode();
  const navAllowed = finderModeAllowsAppNavigation(!!finderMode?.active);

  const barHeight = bottomBarHeight(insets.bottom, envInset);
  const bottomPadding = barHeight - APP_BOTTOM_BAR_HEIGHT;
  const isOnHome = pathname === "/home" || pathname === "/";
  const isOnAnimals =
    pathname === "/my/animals" || pathname.startsWith("/my/animals/");
  const isOnClub = isVereinAreaPath(pathname);
  const isOnCalendar =
    pathname === "/my/calendar" || pathname.startsWith("/my/calendar/");
  const menuCurrent =
    pathname === "/my/profile" ||
    pathname.startsWith("/my/profile/") ||
    pathname === "/my/care-sites" ||
    pathname.startsWith("/my/care-sites/") ||
    pathname === "/my/medicus" ||
    pathname.startsWith("/my/medicus/");

  const iconColor = (active: boolean) =>
    active ? theme.color.primary : theme.color.muted;
  const labelColor = (active: boolean) =>
    active ? theme.color.primary : theme.color.muted;

  const go = (href: string, alreadyThere: boolean) => {
    if (!navAllowed) return;
    if (!alreadyThere) router.push(href as never);
  };

  return (
    <View
      testID="app-bottom-bar"
      style={[
        styles.wrapper,
        {
          backgroundColor: theme.color.bg,
          borderTopColor: theme.color.border,
          paddingBottom: bottomPadding,
          height: barHeight,
          flexShrink: 0,
        },
      ]}
    >
      <View style={styles.bar}>
        <Pressable
          style={styles.item}
          onPress={() => {
            if (!navAllowed) return;
            if (!isOnHome) router.replace("/home" as never);
          }}
          accessibilityRole="button"
          accessibilityLabel="Control Board"
          {...pageCurrent(isOnHome)}
        >
          <HouseIcon size={SIDE_ICON_SIZE} color={iconColor(isOnHome)} />
          <Text style={[styles.label, { color: labelColor(isOnHome) }]}>
            Board
          </Text>
        </Pressable>

        <Pressable
          style={styles.item}
          onPress={() => go(routes.myAnimals, isOnAnimals)}
          accessibilityRole="button"
          accessibilityLabel="Tiere"
          {...pageCurrent(isOnAnimals)}
        >
          <PawPrintIcon size={SIDE_ICON_SIZE} color={iconColor(isOnAnimals)} />
          <Text style={[styles.label, { color: labelColor(isOnAnimals) }]}>
            Tiere
          </Text>
        </Pressable>

        <Pressable
          style={styles.item}
          onPress={() => go(routes.myClub, isOnClub)}
          accessibilityRole="button"
          accessibilityLabel="Verein"
          {...pageCurrent(isOnClub)}
        >
          <UsersThreeIcon size={SIDE_ICON_SIZE} color={iconColor(isOnClub)} />
          <Text style={[styles.label, { color: labelColor(isOnClub) }]}>
            Verein
          </Text>
        </Pressable>

        <Pressable
          style={styles.item}
          onPress={() => go(routes.myCalendar, isOnCalendar)}
          accessibilityRole="button"
          accessibilityLabel="Kalender"
          {...pageCurrent(isOnCalendar)}
        >
          <CalendarBlankIcon
            size={SIDE_ICON_SIZE}
            color={iconColor(isOnCalendar)}
          />
          <Text style={[styles.label, { color: labelColor(isOnCalendar) }]}>
            Kalender
          </Text>
        </Pressable>

        <Pressable
          style={styles.item}
          onPress={() => {
            if (!navAllowed) return;
            openDrawer();
          }}
          accessibilityRole="button"
          accessibilityLabel="Menü"
          {...pageCurrent(menuCurrent)}
        >
          <ListIcon size={SIDE_ICON_SIZE} color={iconColor(menuCurrent)} />
          <Text style={[styles.label, { color: labelColor(menuCurrent) }]}>Menü</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    borderTopWidth: StyleSheet.hairlineWidth,
    width: "100%",
    zIndex: 2,
  },
  bar: {
    height: APP_BOTTOM_BAR_HEIGHT,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
    paddingHorizontal: 4,
  },
  item: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
  },
  label: {
    fontSize: 10,
    fontWeight: "600",
  },
});
